import {defineStore} from 'pinia';
import {SimulationDemand} from '@/models/Demand';
import {Offer} from '@/models/simulation/Simulation';
import SimulationApi from '@/modules/calculator/services/SimulationApi';
import Vue, {computed, ref, watch, watchEffect} from 'vue';
import {
  HomeStartProgram,
  Schedule,
  ScheduleEarlyPayment,
  ScheduleParams,
  ScheduleRow,
  ScheduleRowWithVisible
} from '@/components/calculator/schedule/Schedule';
import {ScheduleVariant} from '@/components/calculator/schedule/models/ScheduleVariant';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {cloneDeep, isInteger, isObject} from 'lodash-es';
import {SimulationProductType, useSimulationStore} from '@/components/calculator/services/SimulationStore';
import { RangeTuple} from '@/services/utils/BasicUtils';
import {ProductType} from '@/commons/enums/ProductType';

const STORE_ID = 'schedule-store';

export const useScheduleStore = defineStore(STORE_ID, () => {
  const userInput = ref<Nullable<SimulationDemand>>(null);
  const offer = ref<Nullable<Offer>>(null);
  const schedule = ref<Nullable<Schedule>>(null);
  const scheduleVariant = ref<ScheduleVariant>(ScheduleVariant.WITHOUT_ADDITIONAL_PAYMENT);
  const earlyPayment = ref<ScheduleEarlyPayment[]>([]);
  const isEveryScheduleRowVisible = ref<boolean>(false);
  const scheduleRows = ref<ScheduleRowWithVisible[]>([]);
  const showAdditionalCosts = ref<boolean>(false);
  const isHomeStartProgram = computed<boolean>(() => !!offer.value?.features?.isHomeStartProgram);
  const simulationProductType = ref<Nullable<ProductType>>(null);


  const earlyPaymentSum = computed<number>(() => scheduleRows.value
    .reduce((acc: number, row: ScheduleRowWithVisible) => acc + (row.earlyPayment ?? 0), 0) ?? 0);

  const loanPeriodInMonths = computed<number>(() => 
    simulationProductType.value === ProductType.CASH 
      ? offer.value?.customizations?.customLoanPeriodInMonths ||  userInput.value?.loanPeriod || 0 
      : offer.value?.customizations?.customLoanPeriodInMonths || userInput.value?.loanPeriodInMonths || 0
  );

  const fetchSchedule = async(simulationId?: string,) => {
    const {simulationId: defaultSimulationId,} = useSimulationStore();

    if (defaultSimulationId) simulationId = defaultSimulationId;

    if (!isObject(offer.value) || !isObject(userInput.value) || !isInteger(scheduleVariant.value)) {
      console.error('Schedule params are not set');
      return;
    }
    try {
      const params = new ScheduleParams(
        offer.value,
        userInput.value,
        scheduleVariant.value,
        earlyPayment.value,
        loanPeriodInMonths.value,
        simulationId!,
        isHomeStartProgram.value,
      );
      schedule.value = simulationProductType.value === 'productTypeCash' ? await SimulationApi.getCashSchedule(params) : await SimulationApi.getSchedule(params);
      setScheduleRows();
    } catch (e) {
      Vue.prototype.errorHandler(e, I18NGetter().useScheduleStore.FETCH_SCHEDULE_ERROR);
    }
  };

  const deleteEarlyPayment = async(index: number) => {
    earlyPayment.value.splice(index, 1);
    await fetchSchedule();
  };
  const addEarlyPayment = async() => {
    if (earlyPayment.value) {
      const DEFAULT_EARLY_PAYMENT_MONTH: number = 10;
      const DEFAULT_EARLY_PAYMENT_VALUE: number = 0;
      const getNextEarlyPaymentMonth = (): number => {
        const earlyPaymentCount: number = earlyPayment.value.length ?? 0;
        const lastEarlyPaymentMonth: number = earlyPayment.value[earlyPaymentCount - 1]?.month ?? 0;
        return earlyPaymentCount ? lastEarlyPaymentMonth + 1 : DEFAULT_EARLY_PAYMENT_MONTH;
      };

      earlyPayment.value.push({
        month: getNextEarlyPaymentMonth(),
        payment: DEFAULT_EARLY_PAYMENT_VALUE,
      });
    }
  };
  const initSchedule = async(
    newOffer: Offer,
    newUserInput: SimulationDemand,
    productType: ProductType
  ) => {
    resetAdditionalCosts();
    if (productType !== simulationProductType.value && simulationProductType.value !== null) resetStoreOnProductTypeChange();
    offer.value = cloneDeep(newOffer);
    userInput.value = cloneDeep(newUserInput);
    isEveryScheduleRowVisible.value = false;
    simulationProductType.value = productType;
    await fetchSchedule();
  };
  const checkAndUpdateRowVisibility = (visibleScheduleRows: RangeTuple[], scheduleRows: ScheduleRowWithVisible[]) => {
    const isOnlyOneRangeOfRowsVisible = visibleScheduleRows.length === 1;
    const isFirstElementVisible = visibleScheduleRows[0][0] === 0;
    const lastElementOfFirstRange = visibleScheduleRows[0][1];
    const lastMonthOfSchedule = scheduleRows[scheduleRows.length - 1].currentMonth;

    if (isOnlyOneRangeOfRowsVisible && isFirstElementVisible && lastElementOfFirstRange === lastMonthOfSchedule) {
      setAllRowsVisible();
    } else {
      makeRowsVisible(visibleScheduleRows, scheduleRows);
    }
  };

  const setAllRowsVisible = () => {
    isEveryScheduleRowVisible.value = true;
    setScheduleRows();
  };

  const makeRowsVisible = (visibleScheduleRows: RangeTuple[], scheduleRows: ScheduleRowWithVisible[]) => {
    visibleScheduleRows.forEach((range: number[]) => {
      for (let i = range[0]; i <= range[1]; i++) {
        scheduleRows[i].visible = true;
      }
    });
    isEveryScheduleRowVisible.value = scheduleRows.every((row: ScheduleRowWithVisible) => row.visible);
  };

  const initScheduleFromParams = async(
    newOffer: Offer,
    params: ScheduleParams,
    variant: ScheduleVariant,
    visibleScheduleRows: RangeTuple[],
    productType: ProductType
  ) => {
    resetAdditionalCosts();
    if (productType !== simulationProductType.value && simulationProductType.value !== null) resetStoreOnProductTypeChange();
    userInput.value = params.userInput ?? null;
    offer.value = newOffer;
    scheduleVariant.value = variant;
    earlyPayment.value = params.earlyPayment ?? [];
    simulationProductType.value = productType
    await fetchSchedule(params.simulationId ?? undefined);
    checkAndUpdateRowVisibility(visibleScheduleRows, scheduleRows.value);
  };

  const setScheduleRows = () => {
    /*
     Ta funkcja steruje widocznością wierszy w tabeli. Wiersze są widoczne, jeśli:
      - są to pierwsze 12 miesięcy kredytu
      - są to miesiące, w których nastąpiła nadpłata (miesiąc przed, miesiąc nadpłaty i miesiąc po)
      - są to miesiące, w których nastąpiła zmiana oprocentowania (miesiąc przed, miesiąc gdzie nastąpiła zmiana i miesiąc po)
      - są to ostatnie 3 miesiące kredytu
     */
    if (isEveryScheduleRowVisible.value) {
      scheduleRows.value = schedule.value!.schedules.map((row) => ({...row, visible: true,}));
    } else {
      // liczby zawsze widocznych wierszy w tabeli
      const NUMBER_OF_FIRST_VISIBLE_ROWS: number = 12;
      const NUMBER_OF_LAST_VISIBLE_ROWS: number = 3;
      // miesiące, w których nastąpiła nadpłata
      const validatedEarlyPayment: ScheduleEarlyPayment[] = earlyPayment.value
        .filter(x => Number.isInteger(x.month)) ?? [];
      const earlyPaymentMonths: number[] = validatedEarlyPayment.map(x => x.month!);
      // miesiąc, w którym następuje zmiana oprocentowania
      const changingMarginInitialPeriod: number = offer.value?.changingMargin?.initialPeriod ?? 0;
      const isVisible = (row: ScheduleRow): boolean => {
        // miesiące sąsiadujące z miesiącami, w których wystąpił powód do wyświetlenia wiersza
        const adjacentMonths: number[] = [row.currentMonth - 1, row.currentMonth + 1,];
        return row.currentMonth <= NUMBER_OF_FIRST_VISIBLE_ROWS ||
          row.currentMonth > schedule.value!.schedules.length - NUMBER_OF_LAST_VISIBLE_ROWS ||
          [row.currentMonth, ...adjacentMonths,]
            .some(x => [...earlyPaymentMonths, changingMarginInitialPeriod + 1,].includes(x));
      };
      scheduleRows.value = schedule.value!.schedules
        .map((row) => ({...row, visible: isVisible(row),}));
    }
  };

  const showNextHiddenRows = (month: number) => {
    /*
      Ta funkcja pokazuje kolejne wiersze w tabeli od miesiąca, który został przekazany jako parametr,
      do kolejnego widocznego wiersza.
     */
    const lastMonth: number = scheduleRows.value[scheduleRows.value.length - 1].currentMonth;
    const nextVisibleMonth: number = scheduleRows.value
      .find(x => x.currentMonth > month && x.visible)?.currentMonth || lastMonth;
    scheduleRows.value.forEach(x => {
      if (x.currentMonth >= month && x.currentMonth < nextVisibleMonth) {
        x.visible = true;
      }
    });
    if (scheduleRows.value.every(x => x.visible)) {
      isEveryScheduleRowVisible.value = true;
    }
  };

  const onShowAllItemsChange = () => {
    isEveryScheduleRowVisible.value = !isEveryScheduleRowVisible.value;
    setScheduleRows();
  };

  const resetStoreOnProductTypeChange = () => {
    earlyPayment.value = []
    scheduleVariant.value = ScheduleVariant.WITHOUT_ADDITIONAL_PAYMENT;
  }

  const resetAdditionalCosts = () => {
    showAdditionalCosts.value = false
  }

  return {
    offer,
    schedule,
    scheduleVariant,
    earlyPayment,
    isEveryScheduleRowVisible,
    scheduleRows,
    earlyPaymentSum,
    userInput,
    loanPeriodInMonths,
    showAdditionalCosts,
    isHomeStartProgram,
    simulationProductType,
    deleteEarlyPayment,
    addEarlyPayment,
    initSchedule,
    initScheduleFromParams,
    showNextHiddenRows,
    onShowAllItemsChange,
    fetchSchedule,
  };
});
